import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { usePreviousRouteStore } from '@pflegia/ui/stores/previous-route.store';

import { ROUTE_CHANGE_ERROR } from '@shared/constants/route-change-error';

const beforeUnload = (unloadEvent: BeforeUnloadEvent) => {
  unloadEvent.preventDefault();
  return '';
};

type ExitIntentPreventerProps = {
  /** Condition for showing the modal */
  condition?: boolean;
  /** Custom modal content (render prop, gets passed open/close functions) */
  children: (props: { onClose: () => void; onStay: () => void }) => ReactNode;
  /** Routes that should not trigger the modal */
  ignoredRoutes?: string[];
  /** Callback to handle the modal opening trigger */
  onTrigger?: (nextUrl: string) => void;
};

export const ExitIntentBlocker: React.FC<ExitIntentPreventerProps> = ({ condition, onTrigger, children, ignoredRoutes = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [historyEntriesCount, setHistoryEntriesCount] = useState(0);
  const [nextUrl, setNextUrl] = useState<string | null>(null);

  const router = useRouter();
  const { previousRoute } = usePreviousRouteStore();
  const isChildShown = useRef(false);
  const initialHistoryLength = useRef(0);

  const handleRouteChangeStart = useCallback(
    (url: string) => {
      if (condition && !isChildShown.current && !ignoredRoutes.some(route => url.startsWith(route))) {
        onTrigger?.(url);
        isChildShown.current = true;
        setNextUrl(url);
        setShowModal(true);
        router.events.emit('routeChangeError');
        throw ROUTE_CHANGE_ERROR;
      }
    },
    [condition, ignoredRoutes, onTrigger, router.events],
  );

  const handlePopState = useCallback(
    (event: PopStateEvent) => {
      if (condition && !isChildShown.current) {
        onTrigger?.(previousRoute || '/');
        event.preventDefault();
        isChildShown.current = true;
        setShowModal(true);
        setNextUrl(previousRoute || '/');

        // Push state to prevent browser back button from working
        window.history.pushState(null, '', window.location.href);
        setHistoryEntriesCount(prev => prev + 1);
      }
    },
    [condition, onTrigger, previousRoute],
  );

  const handleClose = useCallback(() => {
    if (nextUrl) {
      // Calculate how many history entries to go back
      const entriesToGoBack = historyEntriesCount;
      setHistoryEntriesCount(0);
      setShowModal(false);
      isChildShown.current = false;

      // Use setTimeout to ensure state updates complete before navigation
      setTimeout(() => {
        window.history.go(-entriesToGoBack);
        router.replace(nextUrl);
      }, 0);
    }
  }, [nextUrl, historyEntriesCount, router]);

  const handleStay = useCallback(() => {
    isChildShown.current = false;
    setNextUrl(null);
    setShowModal(false);

    // Clean up the extra history entries we added
    if (historyEntriesCount > 0) {
      window.history.go(-historyEntriesCount);
      setHistoryEntriesCount(0);
    }
  }, [historyEntriesCount]);

  useEffect(() => {
    if (!condition) return;

    // Store initial history length on mount
    initialHistoryLength.current = window.history.length;

    const cleanup = () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', beforeUnload);
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', beforeUnload);

    return cleanup;
  }, [condition, handlePopState, handleRouteChangeStart, router.events]);

  // Clean up on unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (historyEntriesCount > 0) {
        window.history.go(-historyEntriesCount);
      }
    };
  }, [historyEntriesCount]);

  return showModal ? children({ onClose: handleClose, onStay: handleStay }) : null;
};
